import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { BookOpen, Eye, EyeOff, Mail, Lock, User, AlertCircle, Loader2, Check, X } from 'lucide-react';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01";

const EbookRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  
  const { register, isAuthenticated, isLoading: authLoading } = useReaderAuth();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password validation rules
  const passwordRules = [
    { test: (p) => p.length >= 8, label: 'At least 8 characters' },
    { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
    { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
    { test: (p) => /[0-9]/.test(p), label: 'One number' },
    { test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;':",.\/<>?]/.test(p), label: 'One special character' },
  ];

  const allRulesPassed = passwordRules.every(rule => rule.test(password));

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else {
        navigate('/ebook');
      }
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesPassed) return;
    
    setError('');
    setIsSubmitting(true);

    const result = await register(email, password, firstName, lastName);
    
    if (result.success) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else {
        navigate('/ebook');
      }
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center p-6">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/ebook" className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <BookOpen size={32} />
          </Link>
          <h1 className="text-2xl font-bold text-[#2c3e50] font-display">
            Create Your Account
          </h1>
          <p className="text-[#5a6c7d] mt-2">
            Get access to your eBook library
          </p>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                    placeholder="First"
                    required
                    data-testid="ebook-register-firstname"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                  placeholder="Last"
                  required
                  data-testid="ebook-register-lastname"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="ebook-register-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2c3e50] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[#2c3e50] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 focus:border-[#d4af37] transition-all"
                  placeholder="Create a strong password"
                  required
                  data-testid="ebook-register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Password Rules */}
            {password && (
              <div className="space-y-1">
                {passwordRules.map((rule, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 text-xs ${
                      rule.test(password) ? 'text-green-600' : 'text-slate-400'
                    }`}
                  >
                    {rule.test(password) ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                    {rule.label}
                  </div>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !allRulesPassed}
              className="w-full py-3 bg-[#d4af37] text-[#2c3e50] font-semibold rounded-xl hover:bg-[#c9a432] focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              data-testid="ebook-register-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#5a6c7d] text-sm">
              Already have an account?{' '}
              <Link 
                to={`/ebook/login${redirectTo ? `?redirect=${redirectTo}` : ''}`}
                className="text-[#d4af37] font-medium hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/ebook" className="text-[#5a6c7d] text-sm hover:text-[#2c3e50]">
            ← Back to eBook
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EbookRegister;
